import { SelectedMenu } from 'rangeConfig/@types';
import { Connectors, CoreApi, ProductType } from '@inter-ikea-storageone/range';
import showDoors from 'rangeConfig/shared/behaviors/showDoors';
import autoFillHandles from 'rangeConfig/shared/behaviors/autoFillHandles';
import dragAddToTarget from 'rangeConfig/shared/behaviors/dragAddToTarget';
import addProduct from 'rangeConfig/shared/behaviors/addProduct';
import removeHandleFromSelectedEntity from 'rangeConfig/shared/behaviors/removeHandleFromSelectedEntity';
import { handlesTypeFilter, colorFilter } from 'rangeConfig/shared/filters/configFilters';
import deleteFrontLayout from 'rangeConfig/shared/behaviors/deleteFrontLayout';
import DoorOpeningDirectionWidget from 'rangeConfig/shared/widgets/doorOpeningDirection/DoorOpeningDirectionWidget';
import withDoorOpeningDirection from 'rangeConfig/shared/widgets/doorOpeningDirection/withDoorOpeningDirection';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import IntegratedHandleWidget from 'rangeConfig/shared/widgets/IntegratedHandleWidget';
import deleteFront from 'rangeConfig/platsa/behaviors/deleteFront';
import withInlineMessage from 'rangeConfig/shared/widgets/withInlineMessage';
import addFrontLayout from 'rangeConfig/platsa/behaviors/addFrontLayout';
import {
  matchDepth,
  matchHandleTypeToFront,
  matchHeight,
  matchWidth
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import {
  matchFrontLayoutToFrame,
  matchFrontType,
  matchHandleSizeToFront,
  matchOrganizerToSelectedInterior
} from 'rangeConfig/platsa/filters/selectedMenuProductFilters';
import {
  hideOpeningDirectionWidget,
  hideSlidingDoorInlineMessage
} from 'rangeConfig/platsa/rules/hideMenuRules';
import {
  hideIntegradeHandlesMessageSelectedMenu,
  isSelectedEntityDrawer
} from 'rangeConfig/shared/rules/hideMenuRules';
import CategoryAdditionalInlineMessage from 'components/ProductSidebar/CategoryWidget/CategoryAdditionalInlineMessage';
import { addProductToSelectedEntity } from 'rangeConfig/shared/behaviors/addProductToSelectedEntity';
import removeOrganizersFromSelectedEntity from 'rangeConfig/platsa/behaviors/removeOrganizersFromSelectedEntity';
import hideDoors from 'rangeConfig/shared/behaviors/hideDoors';
import showHandlesDrillTemplateSheet from 'rangeConfig/shared/behaviors/showHandlesDrillTemplateSheet';
import replaceDoor from 'rangeConfig/shared/behaviors/replaceDoor';

const getMenuTitle = (coreApi: CoreApi) => {
  if (isSelectedEntityDrawer(coreApi)) {
    return 'storageone.selectedProductListCustomiseDrawer';
  } else {
    return 'storageone.selectedProductListCustomiseDoor';
  }
};

const selectedMenu: SelectedMenu = {
  menuTitle: getMenuTitle,
  behaviors: {
    productDeleted: [deleteFront],
    selectedMenuExited: [showHandlesDrillTemplateSheet]
  },
  content: [
    {
      id: 'slidingDoorInlineInfo',
      component: withInlineMessage(
        'storageone.changingFrontCombinationInfo',
        true
      )(CategoryAdditionalInlineMessage),
      hidden: hideSlidingDoorInlineMessage
    },
    {
      id: 'fronts-selected-menu-front-layout',
      title: 'storageone.frontLayoutTabTitle',
      cardsTitle: 'name',
      productThumbToolTip: 'name',
      cardsSubtitle: '',
      behaviors: {
        productDropped: [showDoors, addFrontLayout],
        productClicked: [showDoors, addFrontLayout]
      },
      productTypes: [ProductType.PLATSA_FRONT_LAYOUT],
      productFilters: [matchFrontLayoutToFrame],
      hideInfoButton: true,
      hidePrice: true,
      alternativeSelectedProductList: true,
      noneOption: {
        behaviors: { removeProducts: [deleteFrontLayout] },
        selected: () => false,
        title: 'storageone.productListNoDoorsOptionTitle'
      }
    },
    {
      id: 'fronts-selected-menu-fronts',
      title: 'storageone.frontStyleTabTitle',
      cardsTitle: 'name',
      cardsSubtitle: 'typeName, designText',
      behaviors: { productClicked: [replaceDoor], productDropped: [addProduct] },
      filters: [colorFilter],
      productFilters: [matchWidth, matchHeight, matchDepth, matchFrontType]
    },
    {
      id: 'integratedHandleWidget',
      component: IntegratedHandleWidget,
      hidden: hideIntegradeHandlesMessageSelectedMenu
    },
    {
      id: 'fronts-selected-menu-handles',
      title: 'storageone.productListHandlesTitle',
      cardsTitle: 'name',
      cardsSubtitle: 'typeName, designText, communicatedSizeText',
      productThumbToolTip: 'name, designText',
      behaviors: {
        productDropped: [showDoors, dragAddToTarget],
        productClicked: [showDoors, autoFillHandles]
      },
      productTypes: [ProductType.HANDLE, ProductType.KNOB, ProductType.EDGE_HANDLE],
      filters: [handlesTypeFilter, colorFilter],
      productFilters: [matchHandleSizeToFront, matchHandleTypeToFront],
      noneOption: {
        behaviors: { removeProducts: [removeHandleFromSelectedEntity] },
        selected: noProductsWithTypesForSelectedEntity([
          ProductType.HANDLE,
          ProductType.KNOB,
          ProductType.EDGE_HANDLE
        ]),
        title: 'storageone.productListNoHandlesOptionTitle'
      }
    },
    {
      id: 'doorOpeningDirection',
      title: 'storageone.doorOpeningDirectionTitle',
      component: withDoorOpeningDirection(
        Connectors.PLATSA_FRAME_LEFT_SERIES,
        Connectors.PLATSA_FRAME_RIGHT_SERIES,
        Connectors.PLATSA_FRONT_CONNECTOR_TOP,
        Connectors.PLATSA_FRONT_CONNECTOR_BOTTOM
      )(DoorOpeningDirectionWidget),
      hidden: hideOpeningDirectionWidget
    },
    {
      id: 'drawer-selected-menu-organizers',
      title: 'storageone.selectedProductListAddOrganizers',
      cardsTitle: 'name',
      cardsSubtitle: 'typeName, designText, communicatedSizeText',
      behaviors: {
        productClicked: [addProductToSelectedEntity, hideDoors],
        productDropped: [addProductToSelectedEntity, hideDoors]
      },
      productTypes: [ProductType.PLATSA_SHELF_AND_DRAWER_ORGANIZER],
      productFilters: [matchOrganizerToSelectedInterior],
      noneOption: {
        behaviors: { removeProducts: [removeOrganizersFromSelectedEntity] },
        selected: noProductsWithTypesForSelectedEntity([
          ProductType.PLATSA_SHELF_AND_DRAWER_ORGANIZER
        ]),
        title: 'storageone.productListNoDrawerOrganisersOptionTitle'
      }
    }
  ]
};

export default selectedMenu;
